/**
 * The character PRESENTATION adapter (CHARACTER-PRESENTATION-PRD §5 — P4).
 * The ONE module that knows a character's manifest fields (`src`/`srcFront`/
 * `aspect`) are files and a ratio. Everything that draws a character asks for
 * a POSE by name and gets what its renderer needs: DOM consumers a URL
 * (`view`), canvas consumers a ready image in a feet-anchored box
 * (`presentCharacter`/`drawCharacter`). Nothing outside this module and the
 * producer (lib/cast-packer.js, which WRITES the manifest) references
 * `srcFront` — swept by test (§11, the permission-gate walk precedent).
 *
 * Pose names are truthful to the ART, not §5.1's illustrative list: 'back'
 * is the heckling pose (the crowd faces the streamer — the components' own
 * pk-back class), 'front' the camera-facing view (exit turn, turned zombies,
 * the locker). New poses (walk frames, jaw-open) register HERE and consumers
 * keep naming poses.
 *
 * Portability is a contract, not a unified renderer (PRD §2): this module
 * RESOLVES presentation; the crowd keeps its CSS choreography, and the
 * zombie layer keeps composing FX (the flinch flash) over the resolved box —
 * which is why presentCharacter returns the box rather than always drawing.
 * Pure CJS so jest executes it directly (the heckle-crowd convention).
 */

const POSE_FIELDS = { back: 'src', front: 'srcFront' };

// Fallback for a manifest entry without a measured aspect — the value the
// canvas sites shared (`|| 1.4`) before this module absorbed them.
const DEFAULT_ASPECT = 1.4;

/** URL of `character`'s `pose` view — null for a missing character OR a view
 *  the character doesn't ship (a back-only repack deletes `srcFront`; DOM
 *  consumers gate on the null exactly like the old field read). An unknown
 *  pose THROWS: that is a programmer error, not a data condition. */
function view(character, pose) {
  const field = POSE_FIELDS[pose];
  if (!field) throw new Error(`unknown character pose: ${pose}`);
  return (character && character[field]) || null;
}

/** Does `character` ship `pose`? (The crowd's exit turn plays its front-swap
 *  choreography only when the front asset exists.) */
function hasPose(character, pose) {
  return !!view(character, pose);
}

function aspectOf(character) {
  return (character && character.aspect) || DEFAULT_ASPECT;
}

/** Every view URL a cast can present — the preload warmups (crowd mount,
 *  zombie snapshot receipt) iterate this instead of naming fields. */
function viewsToPreload(cast) {
  const out = [];
  for (const c of cast || []) {
    for (const pose of Object.keys(POSE_FIELDS)) {
      const url = view(c, pose);
      if (url) out.push(url);
    }
  }
  return out;
}

// Same readiness predicate the canvas layer applies to its generic sprites
// (barricade, zombie cycles). Deliberately duplicated 1-line rather than
// exported there→here: generic-sprite plumbing must not depend on the
// CHARACTER adapter.
const imgReady = (img) => !!img && img.complete && img.naturalWidth > 0;

/** The canvas backend's resolution step: `pose` as a READY image in a
 *  feet-anchored box (center `xPx`, feet `feetYPx`, `heightPx`; width follows
 *  the character's own aspect). Returns null while the sprite loads — callers
 *  keep their never-invisible floors (militia silhouette, zombie slab).
 *  `imgs` is the layer's url→HTMLImageElement cache. */
function presentCharacter(imgs, character, pose, { xPx, feetYPx, heightPx }) {
  const url = view(character, pose);
  const img = url ? imgs.get(url) : null;
  if (!imgReady(img)) return null;
  const w = heightPx / aspectOf(character);
  return { img, x: xPx - w / 2, y: feetYPx - heightPx, w, h: heightPx };
}

/** Resolve + draw in one step, for consumers with no FX to compose. True when
 *  the character was drawn; false = paint your fallback. */
function drawCharacter(ctx, imgs, character, pose, box) {
  const p = presentCharacter(imgs, character, pose, box);
  if (!p) return false;
  ctx.drawImage(p.img, p.x, p.y, p.w, p.h);
  return true;
}

module.exports = {
  DEFAULT_ASPECT,
  view,
  hasPose,
  aspectOf,
  viewsToPreload,
  presentCharacter,
  drawCharacter,
};
